import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Move, Type, Mail, FileText, List, Radio, Square, Upload, Save, Eye, ArrowLeft, Layers } from 'lucide-react';
import type { FormField, FormTemplate } from '../../types';
import { formsApi, handleApiError } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import Alert from '../../components/Alert';
import TemplateLoader from '../../components/TemplateLoader';

const FormBuilder: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showTemplateLoader, setShowTemplateLoader] = useState(false);

  // Load existing form data
  useEffect(() => {
    const loadForm = async () => {
      if (!formId) return;
      
      try {
        setLoading(true);
        setError(null);
        const form = await formsApi.getById(formId);
        setFormTitle(form.title);
        setFormDescription(form.description);
        setFormFields(form.fields || []);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [formId]);

  const fieldTypes = [
    { type: 'text' as const, label: 'Text Input', icon: Type },
    { type: 'email' as const, label: 'Email', icon: Mail },
    { type: 'textarea' as const, label: 'Text Area', icon: FileText },
    { type: 'select' as const, label: 'Dropdown', icon: List },
    { type: 'radio' as const, label: 'Radio Buttons', icon: Radio },
    { type: 'checkbox' as const, label: 'Checkboxes', icon: Square },
    { type: 'file' as const, label: 'File Upload', icon: Upload },
  ];

  const addField = (type: FormField['type']) => {
    const newField: FormField = {
      id: Date.now().toString(),
      type,
      label: `New ${type} field`,
      placeholder: type === 'textarea' ? 'Enter your text here...' : 'Enter value...',
      required: false,
      options: type === 'select' || type === 'radio' || type === 'checkbox' ? ['Option 1', 'Option 2'] : undefined,
    };
    setFormFields([...formFields, newField]);
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFormFields(fields => 
      fields.map(field => 
        field.id === id ? { ...field, ...updates } : field
      )
    );
  };

  const deleteField = (id: string) => {
    setFormFields(fields => fields.filter(field => field.id !== id));
  };

  const moveField = (fromIndex: number, toIndex: number) => {
    const newFields = [...formFields];
    const [movedField] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, movedField);
    setFormFields(newFields);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveField(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const saveForm = async () => {
    if (!formId) return;
    
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);
      
      const updateData = {
        title: formTitle,
        description: formDescription,
        fields: formFields,
      };
      
      await formsApi.update(formId, updateData);
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const previewForm = () => {
    if (formId) {
      navigate(`/admin/forms/${formId}/preview`);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const handleLoadTemplate = (template: FormTemplate) => {
    // Replace current form fields with template fields
    setFormFields([...template.fields]);
    // Optionally update form title and description if they're empty
    if (!formTitle) {
      setFormTitle(template.name);
    }
    if (!formDescription) {
      setFormDescription(template.description);
    }
    setShowTemplateLoader(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Form Builder</h1>
            <p className="text-gray-600 mt-2">Create and customize forms for your review events</p>
          </div>
          <button
            onClick={goBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6">
          <Alert type="error" title="Error" message={error} />
        </div>
      )}

      {saveSuccess && (
        <div className="mb-6">
          <Alert type="success" title="Success" message="Form saved successfully!" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Field Types Palette */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Field Types</h2>
            
            {/* Load Template Button */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <button
                onClick={() => setShowTemplateLoader(true)}
                className="w-full flex items-center justify-center p-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                <Layers className="h-4 w-4 mr-2" />
                Load Template
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Start with a pre-built form template
              </p>
            </div>
            
            <div className="space-y-2">
              {fieldTypes.map((fieldType) => {
                const Icon = fieldType.icon;
                return (
                  <button
                    key={fieldType.type}
                    onClick={() => addField(fieldType.type)}
                    className="w-full flex items-center p-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Icon className="h-4 w-4 mr-3 text-gray-500" />
                    {fieldType.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Builder */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow">
            {/* Form Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Form Editor</h2>
                <div className="flex space-x-3">
                  <button
                    onClick={previewForm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </button>
                  <button
                    onClick={saveForm}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Form
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Form Meta */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Title
                  </label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="Enter form title..."
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Description
                  </label>
                  <textarea
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Enter form description..."
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="p-6">
              {formFields.length === 0 ? (
                <div className="text-center py-12">
                  <Type className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No fields added yet</h3>
                  <p className="mt-2 text-gray-600">
                    Click on a field type from the left panel to add it to your form.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {formFields.map((field, index) => (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors cursor-move"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          <Move className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {field.type} Field
                          </span>
                        </div>
                        <button
                          onClick={() => deleteField(field.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Label
                          </label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(field.id, { label: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                          />
                        </div>

                        {field.type !== 'file' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Placeholder
                            </label>
                            <input
                              type="text"
                              value={field.placeholder || ''}
                              onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                          </div>
                        )}

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`required-${field.id}`}
                            checked={field.required}
                            onChange={(e) => updateField(field.id, { required: e.target.checked })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`required-${field.id}`} className="ml-2 block text-sm text-gray-900">
                            Required field
                          </label>
                        </div>

                        {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Options (one per line)
                            </label>
                            <textarea
                              value={field.options?.join('\n') || ''}
                              onChange={(e) => updateField(field.id, { 
                                options: e.target.value.split('\n').filter(option => option.trim()) 
                              })}
                              rows={3}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                              placeholder="Option 1&#10;Option 2&#10;Option 3"
                            />
                          </div>
                        )}
                      </div>

                      {/* Field Preview */}
                      <div className="mt-4 p-4 bg-gray-50 rounded-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        
                        {field.type === 'text' && (
                          <input
                            type="text"
                            placeholder={field.placeholder}
                            disabled
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-white"
                          />
                        )}
                        
                        {field.type === 'email' && (
                          <input
                            type="email"
                            placeholder={field.placeholder}
                            disabled
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-white"
                          />
                        )}
                        
                        {field.type === 'textarea' && (
                          <textarea
                            placeholder={field.placeholder}
                            disabled
                            rows={3}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-white"
                          />
                        )}
                        
                        {field.type === 'select' && (
                          <select disabled className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-white">
                            <option>Select an option...</option>
                            {field.options?.map((option, idx) => (
                              <option key={idx} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                        
                        {field.type === 'radio' && (
                          <div className="space-y-2">
                            {field.options?.map((option, idx) => (
                              <div key={idx} className="flex items-center">
                                <input
                                  type="radio"
                                  name={field.id}
                                  disabled
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                />
                                <label className="ml-2 block text-sm text-gray-900">{option}</label>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {field.type === 'checkbox' && (
                          <div className="space-y-2">
                            {field.options?.map((option, idx) => (
                              <div key={idx} className="flex items-center">
                                <input
                                  type="checkbox"
                                  disabled
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-900">{option}</label>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {field.type === 'file' && (
                          <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
                            <Upload className="mx-auto h-8 w-8 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-600">Click to upload or drag and drop</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Template Loader Modal */}
      <TemplateLoader
        isOpen={showTemplateLoader}
        onClose={() => setShowTemplateLoader(false)}
        onLoadTemplate={handleLoadTemplate}
      />
    </div>
  );
};

export default FormBuilder;